/**
 * Request Manager for Retry Logic and Request Deduplication
 *
 * Provides retry logic with exponential backoff and request deduplication
 * to prevent duplicate concurrent requests and handle transient failures.
 *
 * @module services/shared/RequestManager
 */

import { BaseApiError } from './BaseApiError';

/**
 * Configuration options for retry behavior
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in milliseconds between retries (default: 1000) */
  baseDelay?: number;
  /** Maximum delay in milliseconds between retries (default: 30000) */
  maxDelay?: number;
  /** Custom function to determine if an error should trigger a retry */
  retryCondition?: (error: Error) => boolean;
}

/**
 * Request Manager class for handling retry logic and request deduplication
 */
export class RequestManager {
  /** Map to store pending requests for deduplication */
  private pendingRequests = new Map<string, Promise<unknown>>();

  /**
   * Execute an operation with retry logic and exponential backoff
   *
   * @param operation - The async operation to execute
   * @param options - Retry configuration options
   * @returns Promise that resolves with the operation result
   * @throws The last error if all retries are exhausted
   */
  async executeWithRetry<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 30000,
      retryCondition = this.defaultRetryCondition.bind(this)
    } = options;

    let lastError: Error;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        // Don't retry if we've exhausted attempts or condition fails
        if (attempt === maxRetries || !retryCondition(lastError)) {
          throw lastError;
        }

        // Calculate delay with exponential backoff and jitter
        const delay = this.calculateDelay(attempt, baseDelay, maxDelay);
        await this.sleep(delay);

        attempt++;
      }
    }

    // This should never be reached, but TypeScript requires it
    throw lastError!;
  }

  /**
   * Execute an operation with request deduplication
   *
   * Prevents duplicate concurrent requests by caching and reusing pending promises.
   * Multiple calls with the same key will return the same promise.
   *
   * @param key - Unique identifier for the request
   * @param operation - The async operation to execute
   * @returns Promise that resolves with the operation result
   */
  async executeWithDeduplication<T>(
    key: string,
    operation: () => Promise<T>
  ): Promise<T> {
    // Check if request is already pending
    const existingRequest = this.pendingRequests.get(key);
    if (existingRequest) {
      return existingRequest as Promise<T>;
    }

    // Create new request and cache it
    const request = operation()
      .finally(() => {
        // Clean up the cached request when it completes
        this.pendingRequests.delete(key);
      });

    this.pendingRequests.set(key, request);
    return request;
  }

  /**
   * Default retry condition that checks for retryable errors
   *
   * Retries on:
   * - Network errors (no status code)
   * - Server errors (5xx status codes)
   * - Rate limiting (429 status code)
   * - Request timeout (408 status code)
   *
   * @param error - The error to evaluate
   * @returns true if the error should trigger a retry
   */
  private defaultRetryCondition(error: Error): boolean {
    // Handle BaseApiError instances
    if (error instanceof BaseApiError) {
      const status = error.status;

      // No status code indicates network error - retry
      if (!status) {
        return true;
      }

      // Retry on server errors (5xx), rate limiting (429), and timeouts (408)
      return status >= 500 || status === 429 || status === 408;
    }

    // Handle generic network errors
    const message =
      typeof (error as unknown as { message?: unknown })?.message === 'string'
        ? (error as unknown as { message: string }).message.toLowerCase()
        : '';

    if (error.name === 'NetworkError' ||
        error.name === 'TypeError' ||
        message.includes('fetch') ||
        message.includes('network')) {
      return true;
    }

    // Don't retry by default for unknown errors
    return false;
  }

  /**
   * Calculate delay with exponential backoff and random jitter
   *
   * Uses exponential backoff: delay = baseDelay * (2 ^ attempt)
   * Adds random jitter to prevent thundering herd problem
   *
   * @param attempt - Current attempt number (0-based)
   * @param baseDelay - Base delay in milliseconds
   * @param maxDelay - Maximum delay in milliseconds
   * @returns Calculated delay in milliseconds
   */
  private calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
    // Exponential backoff: 2^attempt
    const exponentialDelay = baseDelay * Math.pow(2, attempt);

    // Cap at maximum delay
    const cappedDelay = Math.min(exponentialDelay, maxDelay);

    // Add random jitter (±25% of the delay)
    const jitter = cappedDelay * 0.25 * (Math.random() * 2 - 1);
    const finalDelay = cappedDelay + jitter;

    // Ensure delay is not negative
    return Math.max(finalDelay, 0);
  }

  /**
   * Sleep utility for introducing delays
   *
   * @param ms - Milliseconds to sleep
   * @returns Promise that resolves after the specified delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear all pending requests (useful for cleanup)
   */
  clearPendingRequests(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get the number of currently pending requests
   */
  getPendingRequestCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Check if a specific request key is currently pending
   */
  isPending(key: string): boolean {
    return this.pendingRequests.has(key);
  }
}

/**
 * Default singleton instance of RequestManager
 */
export const requestManager = new RequestManager();